import AddIcon from "@mui/icons-material/Add";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DownloadIcon from "@mui/icons-material/Download";
import FolderCopyIcon from "@mui/icons-material/FolderCopy";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import swal from "sweetalert";
import ConfirmationModal from "../../components/ConfirmationModal";
import CustomTable from "../../components/CustomTable";
import MainLayout from "../../components/MainLayout";
import {
  LeftPane,
  RightPane,
  SplitViewContainer,
} from "../../components/SplitView";
import axiosInstance from "../../utils/axiosInstance";
import { checkPermission } from "../../utils/checkPermission";
import { extractYoutubeVideoId } from "../../utils/extractYoutubeVideoId";
import {
  capitalizeFirstLetter,
  formatCustomDate,
} from "../../utils/formatters";
import theme from "../../utils/theme";
import ContentModal from "./modal";

export default function Contents() {
  const [refresh, setRefresh] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleModalClose = () => {
    setOpenAdd(false);
    setOpenEdit(false);
    setOpenConfirm(false);
  };

  const handleContentAdd = async (values) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_URL}/contents`,
        values,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.status === 201) {
        setRefresh(!refresh);
        swal("Successfully added a new content.", { icon: "success" });
      }
    } catch (error) {
      swal("Failed to add new content. Please try again.", {
        icon: "error",
      });
    }
  };

  const handleContentEdit = async (values) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_URL}/contents/edit/${selectedContent.id}`,
        values,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.status === 200) {
        setRefresh(!refresh);
        handleModalClose();
        setSelectedContent(null);
        swal("Successfully updated content.", { icon: "success" });
      }
    } catch (error) {
      swal("Failed to update content. Please try again.", { icon: "error" });
    }
  };

  const handleContentDelete = async () => {
    try {
      const response = await axiosInstance.delete(
        `${process.env.REACT_APP_API_URL}/contents/${selectedContent.id}`,
      );

      if (response.status === 204) {
        setRefresh(!refresh);
        handleModalClose();
        setSelectedContent(null);
        swal("Successfully delete content", { icon: "success" });
      }
    } catch (error) {
      swal("Failed to delete content. Please try again.", { icon: "error" });
    }
  };

  const columns = [
    { field: "title", headerName: "Title", width: 400 },
    {
      field: "type",
      headerName: "Type",
      width: 100,
      valueGetter: (value) => capitalizeFirstLetter(value),
      type: "singleSelect",
      valueOptions: ["Infographic", "Article", "Video", "Pdf"],
    },
    {
      field: "category",
      headerName: "Category",
      width: 100,
      valueGetter: (_value, row) => row.category.name,
      sortable: false,
    },
    {
      field: "created_at",
      headerName: "Created At",
      width: 200,
      valueGetter: (value) => value && new Date(value),
      renderCell: (value) => formatCustomDate(value.row.created_at),
      type: "date",
    },
    {
      field: "",
      headerName: "Actions",
      width: 200,
      sortable: false,
      filterable: false,
      type: "actions",
      renderCell: (value) => (
        <Box>
          {checkPermission("content-view") && (
            <Button
              color="info"
              onClick={() => {
                setSelectedContent(value.row);
              }}
            >
              View Details
            </Button>
          )}
        </Box>
      ),
    },
  ];

  // Reset loading state when selectedContent changes
  useEffect(() => {
    if (selectedContent?.type === "video") {
      setLoading(true);
    }
  }, [selectedContent]);

  // Render content preview based on type
  const renderPreview = (content) => {
    switch (content.type) {
      case "article":
        return;

      case "pdf":
        return (
          <>
            {content.fileUrl && (
              <iframe
                title={content.title}
                src={`${process.env.REACT_APP_STORAGE_URL}/${content.fileUrl}`}
                width="100%"
                height="400px"
              ></iframe>
            )}
          </>
        );

      case "video":
        let videoSrc;
        if (content.mediaUrl && content.mediaUrl.startsWith("http")) {
          videoSrc = extractYoutubeVideoId(content.mediaUrl);

          return (
            <>
              <div
                style={{
                  position: "relative",
                  paddingBottom: "50%",
                  height: 0,
                }}
              >
                {/* Show loading indicator if the video is loading */}
                {loading && (
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 9999999,
                    }}
                  >
                    <CircularProgress />
                  </div>
                )}

                <iframe
                  src={`https://www.youtube.com/embed/${videoSrc}`}
                  allowFullScreen
                  onLoad={() => setLoading(false)}
                  onError={() => setLoading(false)}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                  title="YouTube Video"
                />
              </div>

              {/* Display the video link in case the video is not supported for embed */}
              <Typography sx={{ marginTop: 2 }}>
                Watch on source:{" "}
                <a
                  href={content.mediaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {content.mediaUrl}
                </a>
              </Typography>
            </>
          );
        } else {
          videoSrc = `${process.env.REACT_APP_STORAGE_URL}/${content.fileUrl}`;
          return (
            <video width="100%" controls>
              <source src={videoSrc} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          );
        }

      case "infographic":
        return (
          <img
            src={`${process.env.REACT_APP_STORAGE_URL}/${content.coverImageUrl}`}
            alt={content.title}
            width="100%"
          />
        );

      default:
        return <Typography variant="h6">No preview available</Typography>;
    }
  };

  return (
    <>
      <MainLayout>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">Contents</Typography>
          {checkPermission("content-create") && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setOpenAdd(true);
              }}
            >
              Add new
            </Button>
          )}
        </Box>

        <SplitViewContainer theme={theme}>
          {/* Left: Content List */}
          <LeftPane theme={theme}>
            <CustomTable
              columns={columns}
              fetchDataUrl="contents-paginated"
              refresh={refresh}
            />
          </LeftPane>

          <Divider orientation="vertical" />

          {/* Right Pane: Content Preview */}
          <RightPane theme={theme}>
            {selectedContent ? (
              <>
                <Typography variant="h6">{selectedContent.title}</Typography>
                <List>
                  <Tooltip title="Content Type">
                    <ListItem disablePadding>
                      <ListItemIcon>
                        <FolderCopyIcon />
                      </ListItemIcon>
                      <ListItemText primary={selectedContent.type} />
                    </ListItem>
                  </Tooltip>
                  <Tooltip title="Upload Date">
                    <ListItem disablePadding>
                      <ListItemIcon>
                        <CalendarMonthIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={formatCustomDate(selectedContent.created_at)}
                      />
                    </ListItem>
                  </Tooltip>
                  {selectedContent.type === "pdf" && (
                    <Tooltip title="Download Count">
                      <ListItem disablePadding>
                        <ListItemIcon>
                          <DownloadIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={selectedContent.download_count}
                        />
                      </ListItem>
                    </Tooltip>
                  )}
                  <Tooltip title="Views">
                    <ListItem disablePadding>
                      <ListItemIcon>
                        <RemoveRedEyeIcon />
                      </ListItemIcon>
                      <ListItemText primary={selectedContent.view_count} />
                    </ListItem>
                  </Tooltip>
                </List>

                <Typography variant="body2" sx={{ mt: 2 }}>
                  {selectedContent.description}
                </Typography>
                <Divider sx={{ my: 2 }} />
                {selectedContent.type !== "infographic" && (
                  <>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Cover Image
                    </Typography>
                    <img
                      src={`${process.env.REACT_APP_STORAGE_URL}/${selectedContent.coverImageUrl}`}
                      alt={selectedContent.title}
                      width="100%"
                    />
                  </>
                )}
                <Divider sx={{ my: 2 }} />
                {selectedContent.type !== "article" && (
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Preview
                  </Typography>
                )}
                {renderPreview(selectedContent)}

                {/* Action Buttons */}
                {checkPermission("content-update") && (
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2 }}
                    onClick={() => {
                      setOpenEdit(true);
                    }}
                  >
                    Edit
                  </Button>
                )}
                {checkPermission("content-delete") && (
                  <Button
                    variant="outlined"
                    color="error"
                    sx={{ mt: 2, ml: 2 }}
                    onClick={() => {
                      setOpenConfirm(true);
                    }}
                  >
                    Delete
                  </Button>
                )}
              </>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  width: "100%",
                }}
              >
                <Typography variant="body2">
                  Select content to view details
                </Typography>
              </Box>
            )}
          </RightPane>
        </SplitViewContainer>
      </MainLayout>

      <ContentModal
        open={openAdd}
        onClose={handleModalClose}
        onSubmit={handleContentAdd}
      />

      {selectedContent && openEdit && (
        <ContentModal
          open={openEdit}
          onClose={handleModalClose}
          onSubmit={handleContentEdit}
          content={selectedContent}
        />
      )}

      {selectedContent && (
        <ConfirmationModal
          open={openConfirm}
          onClose={handleModalClose}
          onConfirm={handleContentDelete}
          title="Are you sure you want to delete this content?"
          message={
            <span>
              The {capitalizeFirstLetter(selectedContent.type)}{" "}
              <strong>{selectedContent.title}</strong> will be deleted. Please
              note that this action is not reversible.
            </span>
          }
        />
      )}
    </>
  );
}
